"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import toast, { Toaster } from "react-hot-toast";

type ToastType = "success" | "error" | "loading" | "info";

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
  dismissToast: (toastId: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const showToast = useCallback((message: string, type: ToastType = "info") => {
    switch (type) {
      case "success":
        return toast.success(message);
      case "error":
        return toast.error(message);
      case "loading":
        return toast.loading(message);
      case "info":
      default:
        return toast(message);
    }
  }, []);

  const dismissToast = useCallback((toastId: string) => {
    toast.dismiss(toastId);
  }, []);

  const value = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      <Toaster
        position="bottom-right"
        containerStyle={{
          zIndex: 2147483647,
        }}
        toastOptions={{
          duration: 8000,
          style: {
            // background: '#ffffff',
            marginBottom: "60px", // Added margin bottom to move the toast above the menu bar
            color: "#222222",
            fontSize: "14px",
            border: "1px solid #e2e8f0",
            padding: "14px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
          },
          success: {
            iconTheme: {
              primary: "#4CAF50",
              secondary: "#ffffff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#ffffff",
            },
          },
        }}
      />
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
